import { Layer, KonvaEventObject, Stage, Group } from "konva/lib/Core";
import { Node, NodeConfig } from "konva/types/Node";
import { Vector2d } from "konva/types/types";
import { fireStateEvent } from "../../events";
import { Goal, isImported, Row, rowHeight } from "../../shapes/goal";
import { Rect } from "konva/lib/shapes/Rect";
import { SHAPE_SELECTED, SHAPE_UNSELECTED } from "../shape-context-menu";
import {
  findMetricByCursor,
  findMetric,
  orderMetrics,
  shouldReoderMetrics,
  fireShapeEnterEvent,
  getMetricRangeReorder,
  DRAG_END,
  DRAG_ENTER,
  DRAG_MOVE,
  DRAG_START,
  MOUSE_ENTER,
  MOUSE_LEAVE,
  Metric,
} from ".";
import { getChildReoderPayload } from "@gtmhub/goals/utils";
import { Image as KonvaImage } from "konva/lib/shapes/Image";

import metricIconUrl from "wwwroot/img/icons/metric.svg";
import moveIconUrl from "wwwroot/img/icons/move.svg";
import borderIconUrl from "wwwroot/img/icons/bordersvg.svg";

const singleOwnerWidth = 280;
const multipleOwnerWidth = 340;
const DND_DROP_PLACEHOLDER = "dnd-drop-placeholder";
const DND_GROUP = "dnd-group";

function getSortingLayer(stage: Stage): Layer {
  let sortingLayer = stage.findOne<Layer>("#sorting-layer");
  if (!sortingLayer) {
    sortingLayer = new Layer({ id: "sorting-layer" });
    stage.add(sortingLayer);
  }

  return sortingLayer;
}

function createDndPlaceholderGroup(goalShape: Goal, row: Row): Group {
  const width = getRowSizeWidth(goalShape);
  const imageEl = new Image();
  imageEl.src = borderIconUrl;

  const height = rowHeight(row);
  const img = new KonvaImage({
    width: width,
    height: 30,
    name: `${DND_DROP_PLACEHOLDER} inner-contents`,
    x: 5,
    y: height,
    image: imageEl,
  });

  const bgRect = new Rect({ height: height });

  const group = new Group({ name: `${DND_GROUP} inner-contents` });

  group.add(img, bgRect);
  return group;
}

function getDragBoundaries(stage: Stage, goalShape: Goal): { x: number; y: number } {
  const pos = stage.getPointerPosition();
  const x = goalShape.absolutePosition().x;
  const bounce = 28;
  let y = pos.y;

  if (y - bounce >= maxY) {
    y = maxY + bounce;
  }

  if (y <= minY) {
    y = minY;
  }

  return { x, y };
}

function getRowSizeWidth(goalShape: Goal): number {
  return isImported(goalShape.state) ? multipleOwnerWidth : singleOwnerWidth;
}

function addShadowAndBorderToRow(metricRow: Row, goalShape: Goal): Rect {
  if (metricRow.findOne("#background")) {
    return;
  }

  const width = getRowSizeWidth(goalShape);

  const background = new Rect({
    id: "background",
    x: 6,
    y: -5,
    width,
    height: 26,
    fill: "#ffffff",
    cornerRadius: 4,
    stroke: "lightgray",
    strokeWidth: 1,
    shadowColor: "lightgray",
    shadowBlur: 2,
    shadowOffset: { x: 3, y: 3 },
    shadowOpacity: 0.5,
    strokeEnabled: false,
    shadowEnabled: false,
  });

  metricRow.add(background);
  background.setZIndex(-50);

  return background;
}

function addIconBorderToRow(metricRow: Row): Rect {
  if (metricRow.findOne("#iconBorder")) {
    return;
  }

  const border = new Rect({
    fill: "#ffffff",
    stroke: "#9dabc5",
    strokeWidth: 1,
    cornerRadius: 4,
    visible: false,
    id: "iconBorder",
  });

  metricRow.add(border);
  return border;
}

function moveShapeToTarget(target: Node<NodeConfig>, goalShape: Goal, oldPos: Vector2d) {
  target.moveTo(goalShape);
  target.position(oldPos);
}

function removeOnEnterRowStyles(goalShape: Goal): void {
  goalShape.findOne(`.${DND_GROUP}`)?.remove();
}

function getDraggingDirection(dragEnterPos: number, dragEnter: number): string {
  let draggingDirection = "";
  if (dragEnterPos > dragEnter) {
    draggingDirection = "top";
  }

  if (dragEnterPos < dragEnter) {
    draggingDirection = "bot";
  }

  return draggingDirection;
}

function getPreviousRow(metricRow: Row, goalShape: Goal): Row {
  const rows = goalShape.metricRows();
  const rowsArr = rows.toArray();
  const idx = rowsArr.findIndex((r) => r.id() === metricRow.id()) - 1;
  return rows[idx];
}

function restoreMetricRowKrIcon(metricRow: Row): void {
  const icon = metricRow.findOne("#dragging-metric");
  if (icon) {
    metricRow.setIcon(metricIconUrl, "metric");
  }
}

let lastEnteredMetric: Metric, minY: number, maxY: number, endShape: Node<NodeConfig>, startShape: Node<NodeConfig>, oldPos: Vector2d, dragEnterPosition: number;
export const attachOrdereableEventsV2 = (metricRow: Row, goalShape: Goal, stage: Stage, layer: Layer) => {
  /* eslint-disable-next-line sonarjs/cognitive-complexity */
  goalShape.on(SHAPE_SELECTED, () => {
    addShadowAndBorderToRow(metricRow, goalShape);
    addIconBorderToRow(metricRow);

    metricRow.on(MOUSE_ENTER, () => {
      const rows = goalShape.metricRows();

      // look for icon that has not been removed by any chance and remove it explicitly
      rows.each((r) => restoreMetricRowKrIcon(r));

      const icon = metricRow.setIcon(moveIconUrl, "dragging-metric") as KonvaImage;
      icon.draggable(true);
      icon.on(DRAG_START, (e: KonvaEventObject<DragEvent> & { dragEnterPos: number }) => {
        /** fire drag_start on goal shape in order to destroy dynamic text editor before re-ordering metrics */
        goalShape.fire(DRAG_START);

        const iconBorder = metricRow.findOne("#iconBorder");
        if (iconBorder) {
          iconBorder.visible(false);
        }

        const allMetrics = goalShape.state.metrics;
        const background = metricRow.findOne<Rect>("#background");
        background.setAttrs({
          height: rowHeight(metricRow) + 5,
          strokeEnabled: true,
          shadowEnabled: true,
        });

        metricRow.showDropPad();

        minY = stage.findOne(`#metric-row-${allMetrics[0].id}`).getAbsolutePosition().y;
        maxY =
          allMetrics.length > 1 ? stage.findOne(`#metric-row-${allMetrics[allMetrics.length - 2].id}`).getAbsolutePosition().y : metricRow.getAbsolutePosition().y - 33;
        oldPos = metricRow.position();
        startShape = metricRow;
        dragEnterPosition = e.evt.y;

        const sortingLayer = getSortingLayer(stage);
        metricRow.moveTo(sortingLayer);

        /* re-creating the rows without original metricRow that has been moved to temp-layer, necessary if shifting array above without the original row */
        const originalMetric = findMetric(metricRow.id(), allMetrics);
        const filteredMetrics = goalShape.state.metrics.filter((m) => m.id !== originalMetric?.id);
        goalShape.metricRows().each((r) => r.remove());
        goalShape.metrics(filteredMetrics, !!goalShape.state.name);
        goalShape.resize();
        goalShape.draw();
      });

      icon.on(MOUSE_LEAVE, () => {
        const iconBorder = metricRow.findOne("#iconBorder");
        iconBorder.visible(false);
        layer.batchDraw();

        document.body.style.cursor = "default";
      });

      icon.on(MOUSE_ENTER, (evt) => {
        evt.cancelBubble = true; // important to cancel the bubble event - otherwise will mess up with row.mouseEnter event
        const iconBorder = metricRow.findOne("#iconBorder");
        const padding = 4;

        iconBorder.setAttrs({
          visible: true,
          width: icon.width() + padding,
          height: icon.height() + padding * 2,
          x: icon.x() - padding / 2,
          y: icon.y() - (padding * 2) / 2,
        });

        layer.batchDraw();

        document.body.style.cursor = "grab";
      });

      icon.on(DRAG_MOVE, (evt) => {
        const allMetrics = goalShape.state.metrics;
        const shape = findMetricByCursor(allMetrics, stage, layer);

        if (!endShape && shape) {
          fireShapeEnterEvent(shape, dragEnterPosition, evt);
        }

        if (endShape && shape && endShape !== shape) {
          fireShapeEnterEvent(shape, dragEnterPosition, evt);
        }

        endShape = shape;

        const { x, y } = getDragBoundaries(stage, goalShape);

        metricRow.absolutePosition({ y: y, x: x });
        icon.absolutePosition({ y: y, x: x + 10 });

        goalShape.draw();
      });
    });

    metricRow.on(MOUSE_LEAVE, () => {
      // look for icon that has not been removed by any chance and remove it explicitly

      restoreMetricRowKrIcon(metricRow);

      const iconBorders = goalShape.find("#iconBorder");
      iconBorders.each((ib) => ib.visible(false));

      goalShape.draw();
    });

    /** if shape is focused and the click lands the row enable the re-ordering events */
    const pos = stage.getPointerPosition();
    const shape = layer.getIntersection(pos, ".metric-row") as Row;
    if (shape && shape === metricRow) {
      metricRow.fire(MOUSE_ENTER);
    }
  });

  goalShape.on(SHAPE_UNSELECTED, () => {
    metricRow.removeEventListener(MOUSE_ENTER);
    restoreMetricRowKrIcon(metricRow);
  });

  metricRow.on(DRAG_END, (e) => {
    const allMetrics = goalShape.state.metrics;
    const shape = findMetricByCursor(allMetrics, stage, layer);
    const startMetric = findMetric(startShape.id(), allMetrics);
    const endMetric = endShape ? findMetric(endShape.id(), allMetrics) : lastEnteredMetric;
    const background = metricRow.findOne<Rect>("#background");
    const emptySpace = goalShape.findOne(`.${DND_DROP_PLACEHOLDER}`);
    emptySpace?.remove();

    goalShape.fire(SHAPE_SELECTED);

    background.setAttrs({
      strokeEnabled: false,
      shadowEnabled: false,
    });

    if (!shape || shape?.id() === startShape?.id()) {
      moveShapeToTarget(metricRow, goalShape, oldPos);
    }

    if (!shouldReoderMetrics(startMetric, endMetric)) {
      goalShape.resize();
      return;
    }

    moveShapeToTarget(metricRow, goalShape, oldPos);

    const oldIndex = allMetrics.findIndex((m) => m.id === startMetric.id);
    const { from, to } = getMetricRangeReorder(allMetrics, startMetric, endMetric);
    const enrichedOrderedMetrics = allMetrics.map((m) => ({ ...m, childType: "metric" }));
    const movedItem = getChildReoderPayload(goalShape.state.id, {
      item: { id: startMetric.id, childType: "metric" },
      items: enrichedOrderedMetrics,
      oldIndex: oldIndex,
      newIndex: to,
    });

    const orderedMetrics = orderMetrics(enrichedOrderedMetrics, from, to);
    const metricRows = goalShape.metricRows();
    metricRows.each((mr) => mr.remove());

    goalShape.metrics(orderedMetrics, !!goalShape.state.name);
    goalShape.resize();
    goalShape.fire(SHAPE_SELECTED, { shape: goalShape });

    fireStateEvent("stateChange", e, goalShape, { state: goalShape.state, changes: { metrics: orderedMetrics, movedItem } });

    startShape = undefined;
    endShape = undefined;
    oldPos = undefined;
    lastEnteredMetric = undefined;

    // animation.stop();
    goalShape.draw();
  });

  metricRow.on(DRAG_ENTER, (e: KonvaEventObject<DragEvent> & { dragEnterPos: number }) => {
    const pos = stage.getPointerPosition();
    const emptySpace = layer.getIntersection(pos, `.${DND_DROP_PLACEHOLDER}`); /* drag enter should be triggered on metricRow content except the empty-space */
    if (emptySpace) {
      return;
    }

    const draggingDirection = getDraggingDirection(e.dragEnterPos, e.evt.y);
    const previousRow = getPreviousRow(metricRow, goalShape);
    const allMetrics = goalShape.state.metrics;

    lastEnteredMetric = findMetric(e.target.id(), allMetrics);

    removeOnEnterRowStyles(goalShape);
    addOnEnterRowStyles(draggingDirection, metricRow, goalShape, previousRow);

    goalShape.resize();
    goalShape.draw();
  });
};

export const unattachOrdereableEvents = (metricRow: Row): void => {
  metricRow.removeEventListener(DRAG_START);
  metricRow.removeEventListener(DRAG_END);
  metricRow.removeEventListener(DRAG_MOVE);
};

function addOnEnterRowStyles(draggingDirection: string, metricRow: Row, goalShape: Goal, previousRow: Row): Row | Goal {
  let row: Row | Goal,
    isGoalRow = false;

  if (draggingDirection === "top" && !previousRow) {
    row = goalShape.goalRow();
    isGoalRow = true;
  } else if (!previousRow) {
    row = metricRow;
  } else if (draggingDirection === "top" && previousRow) {
    row = previousRow;
  } else {
    row = metricRow;
  }
  const dndDropPlaceholderGroup = createDndPlaceholderGroup(goalShape, row);
  row.add(dndDropPlaceholderGroup);

  /** readjusing Y is necessary since we are most top and empty-space is attached to goal row that is positioned differently */
  if (isGoalRow) {
    dndDropPlaceholderGroup.y(10);
  }

  return row;
}
